<script setup lang="ts">
import NavCreateIcon from '@/assets/icons/NavBar/NavCreateIcon.vue';
import NavHomeIcon from '@/assets/icons/NavBar/NavHomeIcon.vue';
import NavMyRecipesIcon from '@/assets/icons/NavBar/NavMyRecipesIcon.vue';
import NavOrdersIcon from '@/assets/icons/NavBar/NavOrdersIcon.vue';
import NavPlanningIcon from '@/assets/icons/NavBar/NavPlanningIcon.vue';
import NavTimeClockIcon from '@/assets/icons/NavBar/NavTimeClockIcon.vue';
import NavBarItem from '@/components/NavBar/NavBarItem.vue';
import { usePermissions } from '@/composables/usePermissions';
import type { PermissionCategory } from '@/constants/permissions';
import { type MessageSchema } from '@/i18n';
import { useEnvStore } from '@/stores/envStore';
import { useUserStore } from '@/stores/useUserStore';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n<{ message: MessageSchema }>({ useScope: 'global' });
const userStore = useUserStore();
const { can } = usePermissions();

interface RoutePermission {
  category: PermissionCategory;
  actions: string[];
}
const navPermissions: Record<string, RoutePermission> = {
  // '/orders': { category: 'orders', actions: ['view'] },
  '/menu': { category: 'dishes', actions: ['create'] },
  // '/planning': { category: 'planning', actions: ['view'] },
  '/my-recipes': { category: 'recipes', actions: ['view'] },
};

function hasRequiredPermission(path: string): boolean {
  //TODO :  add permissions check in the future, according to the needs
  // return true
  const permissionConfig = navPermissions[path];

  // If no permissions are defined for the route, always show it
  if (!permissionConfig) return true;

  // Check if the user has any of the required permissions
  return permissionConfig.actions.some((action) => can(permissionConfig.category, `${permissionConfig.category}_${action}`));
}

// Compute navbar classes with safe area padding
const navBarClasses = computed(() => {
  const classes = ['fixed bottom-0 z-20 grid w-full max-w-[40rem] grid-flow-col', 'border-t-2 border-primary-300 bg-white text-primary-700'];

  // Environment-specific backgrounds
  if (useEnvStore().environment === 'preprod') {
    classes.push('preprod-mode');
  } else if (useEnvStore().environment === 'local' || useEnvStore().environment === 'development' || useEnvStore().environment === 'staging') {
    classes.push('dev-mode');
  }

  classes.push('pb-safe');

  return classes.join(' ');
});
</script>

<template>
  <nav id="navbar" :class="navBarClasses">
    <NavBarItem to="/" class="onboarding-home">
      <template #icon>
        <NavHomeIcon />
      </template>
      <template #text> {{ t('navBar.home') }} </template>
    </NavBarItem>

    <NavBarItem v-if="userStore.isEmployee" to="/timeclock">
      <template #icon>
        <NavTimeClockIcon />
      </template>
      <template #text> {{ t('navBar.timeclock') }} </template>
    </NavBarItem>

    <!-- <NavBarItem v-if="userStore.isEmployee" to="my-recipes/planned">
      <template #icon>
        <DiningIcon />
      </template>
      <template #text> {{ t('navBar.myRecipes') }} </template>
    </NavBarItem> -->

    <NavBarItem to="/my-recipes" class="onboarding-my-recipes">
      <template #icon>
        <NavMyRecipesIcon />
      </template>
      <template #text> {{ t('navBar.myRecipes') }} </template>
    </NavBarItem>

    <NavBarItem v-if="hasRequiredPermission('/menu')" to="/menu">
      <template #icon>
        <NavCreateIcon />
      </template>
      <template #text> {{ t('navBar.menu') }} </template>
    </NavBarItem>

    <NavBarItem v-if="hasRequiredPermission('/orders')" to="/orders">
      <template #icon>
        <NavOrdersIcon />
      </template>
      <template #text> {{ t('navBar.orders') }} </template>
    </NavBarItem>

    <NavBarItem v-if="hasRequiredPermission('/planning')" to="/planning" class="onboarding-planning">
      <template #icon>
        <NavPlanningIcon />
      </template>
      <template #text> {{ t('navBar.planning') }} </template>
    </NavBarItem>
  </nav>
</template>

<style scoped>
.dev-mode {
  background-color: rgb(169 255 175);
}

.preprod-mode {
  background-color: rgb(243 178 141);
}

/* Keep controls above the system gesture/navigation area exactly once. */
.pb-safe {
  height: calc(4rem + var(--safe-area-inset-bottom, 0px));
  padding-bottom: var(--safe-area-inset-bottom, 0px);
}
</style>
